import { FC } from "react";
import { StepsProps } from "../../types";
import { FormProvider } from "react-hook-form";
import useStep1 from "./useStep1";
import {
  FormInput,
  FormPhoneInput,
  FormSectionHeader,
  FormSelect,
  Loader,
} from "@/components";
import {
  ADDRESS_1,
  ADDRESS_2,
  CITY,
  COMMUNITY_NAME,
  COMMUNITY_REGISTERED_NAME,
  CONTACT_EMAIL,
  EMERGENCY_PHONE,
  GENERAL_PHONE,
  LANGUAGE,
  MANAGER_NAME,
  MANAGER_PHONE,
  STATE,
  UNITS,
  WEBSITE,
  ZIPCODE,
} from "./consts";

const Step1: FC<StepsProps> = ({ isView }) => {
  const {
    form,
    requiredFields,
    stateList,
    managerOptions,
    isManagerLoading,
    stateKey,
    isDetailLoading,
    isDetailRefetching,
    managerKey,
  } = useStep1();
  const { errors } = form.formState;

  return (
    <>
      {isManagerLoading || isDetailLoading || isDetailRefetching ? (
        <Loader />
      ) : (
        <FormProvider {...form}>
          <form>
            <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px]">
              <div>
                <FormSectionHeader>Community Information</FormSectionHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
                  <FormInput
                    name={COMMUNITY_NAME}
                    control={form.control}
                    label="Name"
                    placeholder="Please enter"
                    error={errors[COMMUNITY_NAME]?.message}
                    isRequired={requiredFields.includes(COMMUNITY_NAME)}
                    disabled={isView}
                    maxLength={30}
                    variant="char"
                  />
                  <FormInput
                    name={COMMUNITY_REGISTERED_NAME}
                    control={form.control}
                    label="Community Registered Name"
                    placeholder="Please enter"
                    error={errors[COMMUNITY_REGISTERED_NAME]?.message}
                    isRequired={requiredFields.includes(
                      COMMUNITY_REGISTERED_NAME
                    )}
                    disabled={isView}
                    minLength={3}
                    maxLength={30}
                  />
                  <FormInput
                    name={LANGUAGE}
                    control={form.control}
                    label="Language"
                    placeholder="Please enter"
                    error={errors[LANGUAGE]?.message}
                    isRequired={requiredFields.includes(LANGUAGE)}
                    disabled
                  />
                  <FormInput
                    name={UNITS}
                    control={form.control}
                    label="Units"
                    placeholder="Please enter"
                    error={errors[UNITS]?.message}
                    isRequired={requiredFields.includes(UNITS)}
                    disabled={isView}
                    variant="integer"
                    maxLength={4}
                  />
                </div>
              </div>

              <div className="mt-[40px]">
                <FormSectionHeader>
                  Mailing and Contact Information
                </FormSectionHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
                  <FormInput
                    name={ADDRESS_1}
                    control={form.control}
                    label="Address 1"
                    placeholder="Please enter"
                    error={errors[ADDRESS_1]?.message}
                    isRequired={requiredFields.includes(ADDRESS_1)}
                    disabled={isView}
                  />
                  <FormInput
                    name={ADDRESS_2}
                    control={form.control}
                    label="Address 2"
                    placeholder="Please enter"
                    error={errors[ADDRESS_2]?.message}
                    isRequired={requiredFields.includes(ADDRESS_2)}
                    disabled={isView}
                  />
                  <FormInput
                    name={ZIPCODE}
                    control={form.control}
                    label="Zipcode"
                    placeholder="Please enter"
                    error={errors[ZIPCODE]?.message}
                    isRequired={requiredFields.includes(ZIPCODE)}
                    maxLength={10}
                    variant="integerSpeChar"
                    disabled={isView}
                  />
                  <FormSelect
                    key={stateKey}
                    name={STATE}
                    control={form.control}
                    label="State"
                    placeholder="Please select"
                    error={errors[STATE]?.message}
                    isRequired={requiredFields.includes(STATE)}
                    options={stateList?.map((data: any) => ({
                      label: data.name,
                      value: data.code,
                    }))}
                    disabled={isView}
                  />
                  <FormInput
                    name={CITY}
                    control={form.control}
                    label="City"
                    placeholder="Please enter"
                    error={errors[CITY]?.message}
                    isRequired={requiredFields.includes(CITY)}
                    disabled={isView || !form.watch(STATE)}
                    variant="char"
                  />
                  <FormPhoneInput
                    name={GENERAL_PHONE}
                    control={form.control}
                    label="General Contact Phone"
                    placeholder="Please enter"
                    error={errors[GENERAL_PHONE]?.message}
                    isRequired={requiredFields.includes(GENERAL_PHONE)}
                    disabled={isView}
                  />
                  <FormPhoneInput
                    name={EMERGENCY_PHONE}
                    control={form.control}
                    label="Emergency Contact Phone"
                    placeholder="Please enter"
                    error={errors[EMERGENCY_PHONE]?.message}
                    isRequired={requiredFields.includes(EMERGENCY_PHONE)}
                    disabled={isView}
                  />
                  <FormSelect
                    key={managerKey}
                    name={MANAGER_NAME}
                    control={form.control}
                    label="Community Manager Name"
                    placeholder="Please enter"
                    error={errors[MANAGER_NAME]?.message}
                    isRequired={requiredFields.includes(MANAGER_NAME)}
                    options={managerOptions}
                    disabled={isView}
                  />
                  <FormPhoneInput
                    name={MANAGER_PHONE}
                    control={form.control}
                    label="Community Manager Phone"
                    placeholder="Please enter"
                    error={errors[MANAGER_PHONE]?.message}
                    isRequired={requiredFields.includes(MANAGER_PHONE)}
                    disabled
                  />
                  <FormInput
                    name={CONTACT_EMAIL}
                    control={form.control}
                    label="Contact Email"
                    placeholder="Please enter"
                    error={errors[CONTACT_EMAIL]?.message}
                    isRequired={requiredFields.includes(CONTACT_EMAIL)}
                    disabled={isView}
                  />
                  <FormInput
                    name={WEBSITE}
                    control={form.control}
                    label="Website"
                    placeholder="Please enter"
                    error={errors[WEBSITE]?.message}
                    isRequired={requiredFields.includes(WEBSITE)}
                    disabled={isView}
                  />
                </div>
              </div>
            </div>
          </form>
        </FormProvider>
      )}
    </>
  );
};

export default Step1;
